import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import useAuthorization from './useAuthorization';

export interface TaskCompletionStatus {
  organizationComplete: boolean;
  servicesComplete: boolean;
  equipmentComplete: boolean;
  packagesComplete: boolean;
  playersComplete: boolean;
  paymentComplete: boolean;
  totalTasks: number;
  completedTasks: number;
  hasIncompleteTasks: boolean;
}

export const useTaskCompletion = (): TaskCompletionStatus => {
  const { hasLevel } = useAuthorization();
  const organisation = useSelector((state: RootState) => state.organisation.organisation);
  const players = useSelector((state: RootState) => state.player.players);

  return useMemo(() => {
    // Use setup data from organisation if available, otherwise fallback to checking individual states
    const setup = organisation?.setup;
    
    const organizationComplete = setup?.organisationDetails ?? !!(
      organisation?.name &&
      organisation?.address &&
      organisation?.zipCode &&
      organisation?.city &&
      organisation?.country &&
      organisation?.currency &&
      organisation?.defaultTimezone
    );

    const servicesComplete = setup?.services ?? false;
    const equipmentComplete = setup?.equipment ?? false;
    const packagesComplete = setup?.package ?? false;
    // Consider players task complete if backend setup flag is true OR players exist in Redux
    const playersComplete = (setup?.players ?? false) || ((players?.length || 0) > 0);
    const paymentComplete = setup?.logo ?? false; // Use logo setup status for payment task

    // Define all possible tasks
    const allTasks = [
      { id: 'organization', complete: organizationComplete, requiredLevel: null },
      { id: 'services', complete: servicesComplete, requiredLevel: 'booking' },
      { id: 'equipment', complete: equipmentComplete, requiredLevel: 'booking' },
      { id: 'packages', complete: packagesComplete, requiredLevel: 'booking' },
      { id: 'players', complete: playersComplete, requiredLevel: 'player' },
      { id: 'payment', complete: paymentComplete, requiredLevel: 'booking' },
    ];

    // Filter tasks based on user level
    const availableTasks = allTasks.filter(task => 
      !task.requiredLevel || hasLevel(task.requiredLevel)
    );

    const totalTasks = availableTasks.length;
    const completedTasks = availableTasks.filter(task => task.complete).length;
    const hasIncompleteTasks = completedTasks < totalTasks;

    return {
      organizationComplete,
      servicesComplete,
      equipmentComplete,
      packagesComplete,
      playersComplete,
      paymentComplete,
      totalTasks,
      completedTasks,
      hasIncompleteTasks,
    };
  }, [organisation, players, hasLevel]);
};

export default useTaskCompletion;
